import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { get } from "lodash";

import API_CONSTRUCTOR from "../../models/api-model";
import PropertyAPIs from "../../models/property";
import {
  getBuildingSquareFeet,
  getSmartEstimatedValue,
  calculateEstimatedRent,
  applyListingPriceToProperty,
} from "../../utils/property-calculations";
import { decodeDealFinderCalcQuery } from "../../utils/dealFinderCalcQuery";

import { Skeleton } from "../../components/ui/skeleton";
import { Card, CardContent } from "../../components/ui/card";
import PropertyDetailsModal from "../../components/property-details-modal";
import HouseFlippingCalculator from "../../components/calculator/index";
import GetAgentHelpModal from "../../components/modals/GetAgentHelpModal";

import { PropertyHeader } from "./components/PropertyHeader";
import PropertyGallery from "../../components/property-gallery";
import { PropertyOverview } from "./components/PropertyOverview";
import { NearbyInsightsEnhanced } from "./nearby-insights-enhanced";
import { ComparePropertyEnhanced } from "./compare-property-enhanced";

import {
  AlertCircle,
  ArrowLeft,
  User,
  Download,
  Bed,
  Droplet,
  Home,
  Grid,
  Tag,
  AlertTriangle,
  Building2,
  Sparkles,
  Eye,
  Loader2,
  ArrowRight,
  CheckCircle2,
  DollarSign,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";

const apiModel = API_CONSTRUCTOR();
const propertyApi = new PropertyAPIs(apiModel);

const Property = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const [marketValuePerSqft, setMarketValuePerSqft] = useState(0);
  const [isMarketValuePerSqftManuallyEdited, setIsMarketValuePerSqftManuallyEdited] =
    useState(false);
  const lastSyncedPropertyKeyRef = useRef(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [agentHelpModalOpen, setAgentHelpModalOpen] = useState(false);
  
  // Determine if user is a homeowner
  const isHomeowner = user?.user_role === "homeowner";
  const [existingQuery, setExistingQuery] = useState(null);
  const [checkingQuery, setCheckingQuery] = useState(false);

  const {
    address = "",
    assessedValue = 0,
    city = "",
    county = "",
    loanBalance = 0,
    lotSquareFeet = 0,
    pool = false,
    state = "",
    ownerNames = "",
  } = { ...property };

  const squareFeet = getBuildingSquareFeet(property);
  const estimatedValue = getSmartEstimatedValue(property);
  const estimatedRentAmount = calculateEstimatedRent(
    estimatedValue,
    property?.estimatedRentAmount || 0
  );

  const dealFinderCalc = useMemo(() => {
    const token = searchParams.get("dealCalc");
    if (!token) return null;
    return decodeDealFinderCalcQuery(token);
  }, [searchParams]);

  const setMarketValuePerSqftManually = useCallback((value) => {
    setIsMarketValuePerSqftManuallyEdited(true);
    setMarketValuePerSqft(value);
  }, []);

  const setMarketValuePerSqftAutomatically = useCallback(
    (value) => {
      if (isMarketValuePerSqftManuallyEdited) return;
      setMarketValuePerSqft(value);
    },
    [isMarketValuePerSqftManuallyEdited]
  );

  useEffect(() => {
    const propertyKey =
      propertyId ||
      property?.id ||
      property?.attomId ||
      property?.apn ||
      property?.fips ||
      property?.address ||
      null;

    if (!propertyKey || lastSyncedPropertyKeyRef.current === propertyKey) return;
    lastSyncedPropertyKeyRef.current = propertyKey;
    setIsMarketValuePerSqftManuallyEdited(false);
  }, [propertyId, property]);

  useEffect(() => {
    if (dealFinderCalc?.marketValuePerSqft != null) return;
    if (isMarketValuePerSqftManuallyEdited) return;
    setMarketValuePerSqft(Math.ceil((1.3 * estimatedValue) / squareFeet));
  }, [
    property,
    estimatedValue,
    squareFeet,
    dealFinderCalc,
    isMarketValuePerSqftManuallyEdited,
  ]);

  useEffect(() => {
    if (!dealFinderCalc || dealFinderCalc.marketValuePerSqft == null) return;
    if (isMarketValuePerSqftManuallyEdited) return;
    setMarketValuePerSqft(Math.round(dealFinderCalc.marketValuePerSqft));
  }, [dealFinderCalc, isMarketValuePerSqftManuallyEdited]);

  const calculatorInitialInputs = useMemo(() => {
    if (!property || !dealFinderCalc) return null;
    const est = getSmartEstimatedValue(property);
    const rent =
      dealFinderCalc.currentRental > 0
        ? dealFinderCalc.currentRental
        : calculateEstimatedRent(est, property?.estimatedRentAmount || 0);
    return {
      ...dealFinderCalc,
      lotSize: property.lotSquareFeet || 0,
      currentRental: rent,
      hasPool: property.pool ? "yes" : "no",
    };
  }, [property, dealFinderCalc]);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      setIsLoading(true);
      setProperty(null);
      setError(null);

      try {
        const result = await propertyApi.getPropertyDetails(propertyId);
        const property = get(result, "property", null);
        const meta = get(result, "meta", {});
        const { status } = meta;

        if (property) {
          applyListingPriceToProperty(property);
          setProperty(property);
        }

        if (status !== 200) {
          const { message } = meta;
          setError(message);
        }
      } catch (error) {
        console.error("Error fetching property details:", error);
        setError("Failed to load property details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPropertyDetails();
  }, [propertyId]);

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 sm:p-6 space-y-6">
        {/* Breadcrumb skeleton */}
        <Skeleton className="h-5 w-48" />

        {/* Header skeleton */}
        <div className="space-y-2 pb-6 border-b">
          <Skeleton className="h-10 w-full max-w-md" />
          <Skeleton className="h-5 w-64" />
        </div>

        {/* Image skeleton */}
        <Skeleton className="h-96 w-full rounded-lg" />

        {/* Content skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 sm:p-6">
        <Card className="p-8 shadow-lg">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold mb-2">
                Property Not Found
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mb-6">
                {error}
              </p>
            </div>
            <div className="flex gap-4 justify-center">
              <Link
                to={"/search"}
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Search</span>
              </Link>
              <Link
                to={"/"}
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary hover:underline font-medium"
              >
                <span>Go to Home</span>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-8">
      {/* Property Header */}
      <PropertyHeader
        address={address}
        city={city}
        state={state}
        county={county}
        onDetailsClick={() => setDetailsModalOpen(true)}
      />

      {/* Property Gallery & Key Metrics - Two Column Layout (35-65 ratio) */}
      <div className="grid grid-cols-1 lg:grid-cols-[35%_65%] gap-6">
        {/* Left Column - Gallery (35%) */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <PropertyGallery property={property} />
        </div>

        {/* Right Column - Property Details (65%) */}
        <div className="space-y-4">
          {/* Owner Information */}
          <Card className="shadow-md hover:shadow-lg transition-shadow bg-white">
            <CardContent className="pt-6">
              <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                Owner Information
              </h3>
              <div className="p-3 bg-muted/50 rounded-lg flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Property Owner
                  </p>
                  <p
                    className="text-sm sm:text-base font-semibold truncate"
                    title={ownerNames}
                  >
                    {ownerNames}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Property Features */}
          <Card className="shadow-md hover:shadow-lg transition-shadow bg-white">
            <CardContent className="pt-6">
              <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
                <Download className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                Property Features
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {/* Area */}
                <div className="text-center p-2 sm:p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
                  <Download className="h-4 w-4 sm:h-5 sm:w-5 mx-auto mb-1 text-primary" />
                  <p className="text-xs text-muted-foreground mb-1">Area</p>
                  <p className="text-xs sm:text-sm font-bold">
                    {squareFeet?.toLocaleString() || "N/A"} sq ft
                  </p>
                </div>

                {/* Bedrooms */}
                <div className="text-center p-2 sm:p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
                  <Bed className="h-4 w-4 sm:h-5 sm:w-5 mx-auto mb-1 text-primary" />
                  <p className="text-xs text-muted-foreground mb-1">Bedrooms</p>
                  <p className="text-xs sm:text-sm font-bold">
                    {property?.bedrooms || "N/A"}
                  </p>
                </div>

                {/* Bathrooms */}
                {property?.bathrooms && (
                  <div className="text-center p-2 sm:p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
                    <Droplet className="h-4 w-4 sm:h-5 sm:w-5 mx-auto mb-1 text-primary" />
                    <p className="text-xs text-muted-foreground mb-1">
                      Bathrooms
                    </p>
                    <p className="text-xs sm:text-sm font-bold">
                      {property?.bathrooms}
                    </p>
                  </div>
                )}

                {/* Pool */}
                <div className="text-center p-2 sm:p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
                  <Droplet className="h-4 w-4 sm:h-5 sm:w-5 mx-auto mb-1 text-primary" />
                  <p className="text-xs text-muted-foreground mb-1">Pool</p>
                  <p className="text-xs sm:text-sm font-bold">
                    {pool ? "Yes" : "No"}
                  </p>
                </div>

                {/* Year Built */}
                {property?.yearBuilt && (
                  <div className="text-center p-2 sm:p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
                    <Home className="h-4 w-4 sm:h-5 sm:w-5 mx-auto mb-1 text-primary" />
                    <p className="text-xs text-muted-foreground mb-1">
                      Year Built
                    </p>
                    <p className="text-xs sm:text-sm font-bold">
                      {property?.yearBuilt}
                    </p>
                  </div>
                )}

                {/* Lot Size */}
                {lotSquareFeet && (
                  <div className="text-center p-2 sm:p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
                    <Grid className="h-4 w-4 sm:h-5 sm:w-5 mx-auto mb-1 text-primary" />
                    <p className="text-xs text-muted-foreground mb-1">
                      Lot Size
                    </p>
                    <p className="text-xs sm:text-sm font-bold">
                      {lotSquareFeet?.toLocaleString() || "N/A"} sq ft
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Property Status */}
          <Card className="shadow-md hover:shadow-lg transition-shadow bg-white">
            <CardContent className="pt-6">
              <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                Property Status
              </h3>
              <div className="flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium ${
                    property?.onMarket
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  <Tag className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  {property?.onMarket ? "On Market" : "Off Market"}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium ${
                    property?.vacant
                      ? "bg-red-100 text-red-700"
                      : "bg-green-100 text-green-700"
                  }`}
                >
                  <Home className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  {property?.vacant ? "Vacant" : "Occupied"}
                </span>
                {property?.bankruptcy && (
                  <span className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium bg-red-100 text-red-700">
                    <AlertCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    Bankruptcy
                  </span>
                )}
                {property?.preForeclosure && (
                  <span className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium bg-orange-100 text-orange-700">
                    <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    Pre-Foreclosure
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-700">
                  <Building2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  {property?.propertyType || "Residential"}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>


      {/* Full Property Overview Section */}
      <section className="space-y-6">
        <PropertyOverview
          property={property}
          squareFeet={squareFeet}
          estimatedValue={estimatedValue}
          estimatedRentAmount={estimatedRentAmount}
        />
      </section>

      {/* Divider */}
      <div className="border-t"></div>

      {/* Investment Calculator Section */}
      <section className="space-y-6">

        <HouseFlippingCalculator
          key={propertyId}
          assessedValue={assessedValue}
          buildingSquareFeet={squareFeet}
          estimatedRentAmount={estimatedRentAmount}
          estimatedValue={estimatedValue}
          loanBalance={loanBalance}
          lotSquareFeet={lotSquareFeet}
          marketValuePerSqft={marketValuePerSqft}
          owner={ownerNames}
          pool={pool}
          setMarketValuePerSqft={setMarketValuePerSqftManually}
          setMarketValuePerSqftAuto={setMarketValuePerSqftAutomatically}
          property={property}
          propertyId={propertyId}
          isHomeowner={isHomeowner}
          showCompare={true}
          initialInputs={calculatorInitialInputs}
        />
      </section>

      {/* Start Your Flip CTA - After calculator */}
      <Card className="bg-gradient-to-br from-primary/5 via-orange-500/5 to-orange-600/5 border-primary/20 shadow-xl hover:shadow-2xl transition-all">
        <CardContent className="p-6 sm:p-8">
          {checkingQuery ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
              <p className="text-muted-foreground">Checking your submission status...</p>
            </div>
          ) : existingQuery ? (
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  Request Already Submitted!
                </h3>
                <p className="text-muted-foreground mb-1">
                  You've already submitted a request for this property.
                </p>
                <p className="text-sm text-muted-foreground">
                  Submitted on {new Date(existingQuery.created_at).toLocaleDateString()}
                </p>
              </div>
              <Button
                onClick={() => navigate(`/queries/${existingQuery.id}`)}
                size="lg"
                className="gap-2 bg-gradient-to-r from-green-600 to-green-500 text-white hover:from-green-700 hover:to-green-600 shadow-lg text-base sm:text-lg font-semibold px-8 py-6"
              >
                <Eye className="h-5 w-5" />
                View Your Request Details
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Header */}
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                  <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary via-orange-500 to-orange-600 bg-clip-text text-transparent">
                     Start Your Property Flip
                  </h3>
                </div>
                <p className="text-muted-foreground text-base sm:text-lg max-w-3xl mx-auto">
                  {isHomeowner 
                    ? "Is this your property? Request a personalized offer and receive renovation plans, guaranteed returns, and up to 2 years rent upfront—delivered within 48 hours"
                    : "Request a professional analysis of this property including detailed renovation plans, cost breakdowns, and comprehensive profit projections"
                  }
                </p>
              </div>

              {/* Benefits Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-4 bg-white/50 rounded-lg border border-primary/10">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">
                      {isHomeowner ? "Upfront Payment" : "ROI Analysis"}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {isHomeowner 
                        ? "Get up to 2 years rent upfront"
                        : "Detailed profit projections"
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-white/50 rounded-lg border border-primary/10">
                  <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                    <Home className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">
                      {isHomeowner ? "Free Renovations" : "Renovation Plan"}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {isHomeowner 
                        ? "We cover all renovation costs"
                        : "Complete renovation breakdown"
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-white/50 rounded-lg border border-primary/10">
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">
                      {isHomeowner ? "15% Return Guarantee" : "Market Analysis"}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {isHomeowner 
                        ? "Guaranteed minimum return"
                        : "Comprehensive market insights"
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Timeline Badge */}
              <div className="flex justify-center">
                <Badge className="px-4 py-2 text-sm bg-primary/10 text-primary border-primary/20">
                  ⚡ Response within 48 hours
                </Badge>
              </div>

              {/* CTA Button */}
              <div className="text-center">
                <Button
                  onClick={() => setAgentHelpModalOpen(true)}
                  size="lg"
                  className="gap-2 bg-gradient-to-r from-primary to-orange-600 text-white hover:from-primary/90 hover:to-orange-500 shadow-lg hover:shadow-xl transition-all text-base sm:text-lg font-bold px-8 sm:px-12 py-6 sm:py-7 group"
                >
                  <Sparkles className="h-5 w-5 sm:h-6 sm:w-6" />
                  Let's Koflip together
                  <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-1 transition-transform" />
                </Button>
                <p className="text-xs text-muted-foreground mt-3">
                  {isHomeowner 
                    ? "🏠 Homeowners get priority review • No obligations • Free consultation"
                    : "Free consultation • No obligations • Professional analysis"
                  }
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Divider */}
      <div className="border-t"></div>

      {/* Divider */}
      <div className="border-t"></div>

      {/* Nearby Properties Section */}
      <section className="space-y-6">
        <NearbyInsightsEnhanced
          property={property}
          propertyId={propertyId}
          setMarketValuePerSqft={setMarketValuePerSqftAutomatically}
          skipAutoMarketValuePerSqft={Boolean(dealFinderCalc)}
        />
      </section>

      {/* Property Details Modal */}
      <PropertyDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        property={property}
      />

      {/* Get Agent Help Modal */}
      <GetAgentHelpModal
        isOpen={agentHelpModalOpen}
        onClose={() => setAgentHelpModalOpen(false)}
        propertyData={property}
        propertyAddress={address}
        onSuccess={(query) => {
          console.log('Query created:', query);
          setExistingQuery(query);
          // Optionally redirect to queries page
          // navigate('/my-queries');
        }}
      />
    </div>
  );
};

export default Property;
